import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, MapPin, Cake, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useLang } from "../lib/i18n";
import { useAppData } from "../context/AppDataContext";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { GoogleButton } from "./GoogleButton";

const dialogCls = "w-[calc(100%-2rem)] max-w-md rounded-3xl border-0 p-6";

export function LoginPromptDialog() {
  const { loginPrompt, setLoginPrompt } = useAppData();
  const { t } = useLang();
  const navigate = useNavigate();
  const go = (path) => {
    setLoginPrompt(false);
    navigate(path, { state: { from: window.location.pathname } });
  };
  return (
    <Dialog open={loginPrompt} onOpenChange={setLoginPrompt}>
      <DialogContent className={dialogCls} data-testid="login-prompt-dialog">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10">
          <Lock className="h-6 w-6 text-brand" />
        </div>
        <DialogTitle className="text-center text-xl font-extrabold text-navy">{t("auth.required")}</DialogTitle>
        <DialogDescription className="text-center text-sm text-slate-500">{t("auth.requiredSub")}</DialogDescription>
        <div className="mt-2 space-y-2.5">
          <button className="btn-primary w-full" onClick={() => go("/login")} data-testid="login-prompt-login-btn">
            {t("auth.login")}
          </button>
          <button className="btn-outline w-full" onClick={() => go("/registro")} data-testid="login-prompt-register-btn">
            {t("auth.register")}
          </button>
          <GoogleButton testid="login-prompt-google-btn" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ZoneDialog({ open, onOpenChange }) {
  const { zones, zone, setZone, zoneName } = useAppData();
  const { t } = useLang();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={dialogCls} data-testid="zone-dialog">
        <DialogTitle className="text-xl font-extrabold text-navy">{t("zoneDialog.title")}</DialogTitle>
        <DialogDescription className="text-sm text-slate-500">{t("zoneDialog.sub")}</DialogDescription>
        <div className="grid max-h-[55dvh] grid-cols-2 gap-2 overflow-y-auto">
          {zones.map((z) => (
            <button
              key={z.key}
              onClick={async () => {
                await setZone(z.key);
                onOpenChange(false);
              }}
              data-testid={`zone-option-${z.key}`}
              className={`flex items-center gap-2 rounded-2xl border px-3 py-3 text-left text-sm font-semibold transition-colors duration-200 ${
                zone === z.key ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-navy hover:border-brand/50"
              }`}
            >
              <MapPin className="h-4 w-4 shrink-0 text-alert" />
              <span className="flex-1 truncate">{zoneName(z.key)}</span>
              {zone === z.key && <Check className="h-4 w-4" />}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BirthDateDialog() {
  const { user, updateMe } = useAuth();
  const { t } = useLang();
  const skipKey = user ? `mm_birth_skip_${user.user_id}` : null;
  const [skipped, setSkipped] = useState(() => (skipKey ? !!localStorage.getItem(skipKey) : false));
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!user && !user.birth_date && !skipped && !(skipKey && localStorage.getItem(skipKey));

  const skip = () => {
    localStorage.setItem(skipKey, "1");
    setSkipped(true);
  };
  const save = async () => {
    if (!value) return setError(t("err.required"));
    setBusy(true);
    try {
      await updateMe({ birth_date: value });
    } catch (e) {
      setError(apiError(e, t));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && skip()}>
      <DialogContent className={dialogCls} data-testid="birthdate-dialog">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10">
          <Cake className="h-6 w-6 text-brand" />
        </div>
        <DialogTitle className="text-center text-xl font-extrabold text-navy">{t("birth.title")}</DialogTitle>
        <DialogDescription className="text-center text-sm text-slate-500">{t("birth.sub")}</DialogDescription>
        <input
          type="date"
          className="field"
          value={value}
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setValue(e.target.value)}
          data-testid="birthdate-input"
        />
        {error && <p className="text-sm text-alert" data-testid="birthdate-error">{error}</p>}
        <div className="flex gap-2.5">
          <button className="btn-outline flex-1" onClick={skip} data-testid="birthdate-skip-btn">{t("birth.skip")}</button>
          <button className="btn-primary flex-1" onClick={save} disabled={busy} data-testid="birthdate-save-btn">{t("birth.save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
